import { useEffect, useState } from "react";
import type { BranchHour } from "@dentacore/shared-types";

import styles from "./settings.module.css";

/**
 * ISO-8601 weekdays with the names a reader expects, in reading order.
 *
 * **Not clinic data.** A weekday name is a property of the calendar, not of a
 * practice — unlike a branch name, a chair, an hour or a price, no admin has an
 * opinion about whether Monday exists.
 */
const WEEKDAYS: readonly { dayOfWeek: number; label: string; short: string }[] = [
  { dayOfWeek: 1, label: "Monday", short: "Mon" },
  { dayOfWeek: 2, label: "Tuesday", short: "Tue" },
  { dayOfWeek: 3, label: "Wednesday", short: "Wed" },
  { dayOfWeek: 4, label: "Thursday", short: "Thu" },
  { dayOfWeek: 5, label: "Friday", short: "Fri" },
  { dayOfWeek: 6, label: "Saturday", short: "Sat" },
  { dayOfWeek: 7, label: "Sunday", short: "Sun" },
];

/**
 * What the editor shows a location that has never had hours set (QA20-10).
 *
 * **Times are deliberately absent.** Marking the weekend shut is a layout
 * default a practice changes with one click; putting `09:00` in the Monday box
 * would be inventing a clinic's opening hours, which is the thing *Data is
 * configured, not coded* forbids outright. The two copy buttons already solve
 * the typing, from a day the reader filled in themselves.
 */
const WEEKEND_CLOSED: readonly BranchHour[] = WEEKDAYS.map(({ dayOfWeek }) => ({
  dayOfWeek,
  opensAt: null,
  closesAt: null,
  isClosed: dayOfWeek === 6 || dayOfWeek === 7,
}));

/**
 * When this location's day starts and ends, per weekday.
 *
 * **These draw the calendar; they do not gate it** (decision H2, 2026-08-25).
 * A booking may land at any time whatever this says, so the panel describes
 * what it changes — where the grid opens — rather than implying a rule it does
 * not enforce. Saying "we are open 09:00–17:00" and then refusing a 17:30
 * emergency is the behaviour the client removed.
 *
 * **One opens/closes pair per weekday** (H1) — there are no lunch breaks, so
 * there is no add-a-segment control and no row can hold two spans.
 *
 * The editor holds the whole week and saves it in one write, matching the
 * endpoint. A row is either shut, fully filled in, or empty; half a pair is
 * refused by the server, and this keeps the reader out of that state by
 * clearing both when either is cleared.
 */
export function HoursPanel({
  hours,
  busy,
  onSave,
}: {
  hours: readonly BranchHour[] | null;
  busy: boolean;
  onSave: (days: BranchHour[]) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<readonly BranchHour[] | null>(hours);

  /**
   * The server's answer replaces the draft — including after a save, so a week
   * the server normalised is what the reader sees rather than what they typed.
   *
   * **A location with no hours at all opens with the weekend marked closed**
   * (QA20-10, 2026-09-20). The client asked for it and the line worth being
   * careful about is where the default lives: *Data is configured, not coded*
   * names opening hours explicitly, and plenty of practices here open on
   * Saturday.
   *
   * So this is **a starting position in the form and nothing else**. Nothing is
   * written until Save, both days are one click from reopening, and no branch
   * is ever created carrying an opinion about the working week.
   *
   * **The test is `neverSet`, not `hours.length`**, and getting that wrong is
   * what this comment exists to stop somebody repeating: the API fills every
   * missing weekday in, so a location that has never had hours still comes back
   * as seven rows and a length check never fires. What says "never set" is that
   * every row is blank *and* not closed — the same condition the note below the
   * heading already uses to explain the 08:00–18:00 floor.
   *
   * A practice that has saved anything at all keeps what it saved: times make
   * `neverSet` false, and so does a day someone deliberately marked shut.
   */
  useEffect(() => {
    if (hours === null) {
      setDraft(null);
      return;
    }
    const neverSet = hours.every((row) => row.opensAt === null && !row.isClosed);
    setDraft(neverSet ? WEEKEND_CLOSED : hours);
  }, [hours]);

  if (draft === null) {
    return (
      <section className={styles.card}>
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Opening hours</h2>
        </div>
        <div className={styles.cardBody}>
          <p className={styles.quiet}>Reading&hellip;</p>
        </div>
      </section>
    );
  }

  const rowFor = (dayOfWeek: number): BranchHour =>
    draft.find((row) => row.dayOfWeek === dayOfWeek) ?? {
      dayOfWeek,
      opensAt: null,
      closesAt: null,
      isClosed: false,
    };

  const amend = (dayOfWeek: number, change: Partial<BranchHour>): void => {
    setDraft(
      WEEKDAYS.map(({ dayOfWeek: day }) => {
        const row = rowFor(day);
        return day === dayOfWeek ? { ...row, ...change } : row;
      }),
    );
  };

  /** Blank is null, not `""` — the wire type says "no time", not "empty text". */
  const setTime = (dayOfWeek: number, field: "opensAt" | "closesAt", value: string): void => {
    amend(dayOfWeek, { [field]: value === "" ? null : value, isClosed: false });
  };

  /**
   * Copy one day across the rest of the week.
   *
   * **A practice types the same two times seven times**, which is the client's
   * complaint verbatim: *"it's really annoying having to select the time 7
   * times."* Almost every clinic has one weekday pattern and closes at the
   * weekend, so the two buttons below are the two answers worth having.
   *
   * **They copy the day whose times are filled in first, not a value this
   * file chose.** There is no default 09:00 anywhere here — the source is
   * whatever the reader has already typed, so this stays a labour-saver rather
   * than becoming the hardcoded opening hours *Data is configured, not coded*
   * forbids. With nothing typed yet, both buttons are disabled and say why.
   */
  const source = WEEKDAYS.map(({ dayOfWeek }) => rowFor(dayOfWeek)).find(
    (row) => row.opensAt !== null && row.closesAt !== null,
  );

  const applyFrom = (days: number[]): void => {
    if (source === undefined) return;
    setDraft(
      WEEKDAYS.map(({ dayOfWeek: day }) => {
        const row = rowFor(day);
        if (!days.includes(day)) return row;
        return { ...row, opensAt: source.opensAt, closesAt: source.closesAt, isClosed: false };
      }),
    );
  };

  const sourceLabel =
    source === undefined
      ? null
      : (WEEKDAYS.find((d) => d.dayOfWeek === source.dayOfWeek)?.label ?? null);

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        {/*
          **The card says what the pane's own lead does not.** The pane above
          already explains that hours draw the grid rather than gate a booking
          (decision H2); repeating it here printed the same sentence twice at
          two sizes. What is left is the part only this card can tell you —
          where the floor sits, and whether anything has been set at all.
        */}
        <div>
          <h2 className={styles.cardTitle}>The week</h2>
        </div>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.rowActions}>
          <button
            type="button"
            className={styles.ghost}
            disabled={busy || source === undefined}
            onClick={() => applyFrom([1, 2, 3, 4, 5, 6, 7])}
            data-testid="hours-copy-all"
          >
            Use these times every day
          </button>
          <button
            type="button"
            className={styles.ghost}
            disabled={busy || source === undefined}
            onClick={() => applyFrom([1, 2, 3, 4, 5])}
            data-testid="hours-copy-weekdays"
          >
            Monday to Friday only
          </button>
          <span className={styles.suffix}>
            {source === undefined
              ? "Fill in one day, then copy it across."
              : `Copies ${sourceLabel ?? "the first filled-in day"}'s times.`}
          </span>
        </div>

        {/*
        **Seven columns, not seven rows** (QA20-10): *"let's have the opening
        hours screen displayed horizontally … and let's narrow down the gap so
        they can be seen without scrolling sideways."*

        A week is a row of days everywhere else a person meets one — a calendar,
        a rota, a diary — and stacking it made comparing Tuesday with Thursday a
        scroll. The grid is `repeat(7, minmax(0, 1fr))` so the columns share the
        width evenly and none of them can push the others off; `minmax(0, …)` is
        what stops a `<input type="time">`'s intrinsic width doing exactly that.

        Below `--hours-stack` it returns to one day per row, because seven time
        pairs genuinely do not fit on a phone and a horizontal scrollbar is the
        thing this was asked to remove.
      */}
        <ul className={styles.week} data-testid="hours-list">
          {WEEKDAYS.map(({ dayOfWeek, label, short }) => {
            const row = rowFor(dayOfWeek);

            return (
              <li
                key={dayOfWeek}
                className={row.isClosed ? `${styles.day} ${styles.dayShut}` : styles.day}
                data-testid={`hours-row-${String(dayOfWeek)}`}
              >
                {/*
                The short name is what is drawn and the full one is what is
                announced — seven columns have no room for "Wednesday", and a
                screen reader hearing "Wed" is being given an abbreviation to
                decode for no reason.
              */}
                <span className={styles.dayName} aria-hidden="true">
                  {short}
                </span>
                <span className={styles.srOnly}>{label}</span>

                <input
                  type="time"
                  className={styles.dayTime}
                  aria-label={`${label} opens at`}
                  value={row.opensAt ?? ""}
                  disabled={busy || row.isClosed}
                  onChange={(event) => setTime(dayOfWeek, "opensAt", event.target.value)}
                  data-testid={`hours-opens-${String(dayOfWeek)}`}
                />
                <input
                  type="time"
                  className={styles.dayTime}
                  aria-label={`${label} closes at`}
                  value={row.closesAt ?? ""}
                  disabled={busy || row.isClosed}
                  onChange={(event) => setTime(dayOfWeek, "closesAt", event.target.value)}
                  data-testid={`hours-closes-${String(dayOfWeek)}`}
                />
                <button
                  type="button"
                  className={styles.dayShutToggle}
                  disabled={busy}
                  onClick={() =>
                    amend(dayOfWeek, {
                      isClosed: !row.isClosed,
                      opensAt: null,
                      closesAt: null,
                    })
                  }
                  data-testid={`hours-shut-${String(dayOfWeek)}`}
                >
                  {/*
                    **"Mark closed", not "Closed".** The label was a state where
                    the control is an action, on a tile whose two time boxes are
                    empty until somebody fills them — so a weekday that is simply
                    unset read as a weekday that had been closed on purpose, and
                    the button that would close it looked like the label saying
                    it already was.
                  */}
                  {row.isClosed ? "Reopen" : "Mark closed"}
                </button>
              </li>
            );
          })}
        </ul>

        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => onSave([...draft])}
            data-testid="hours-save"
          >
            Save hours
          </button>
        </div>
      </div>
    </section>
  );
}
